import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CategorySwitcher from './CategorySwitcher';

const MAPS_URL =
  'https://www.google.com/maps/dir/?api=1&destination=Cvetkovci%2050%2C%202273%20Podgorci';

const LOGO_IMAGE =
  'https://static.readdy.ai/image/610b801a81a299b5cf2b47908013d228/b02d261c94c70eb29694e4d94e5f7f25.png';

const homeLinks = [
  { id: 'domov', label: 'DOMOV' },
  { id: 'o-nas', label: 'O NAS' },
  { id: 'ponudba', label: 'PONUDBA' },
  { id: 'galerija', label: 'GALERIJA' },
  { id: 'dozivetje', label: 'DOŽIVETJE' },
  { id: 'kontakt', label: 'KONTAKT' },
];

const roomLinks = [
  { id: 'domov', label: 'DOMOV' },
  { id: 'sobe', label: 'SOBE' },
  { id: 'oprema', label: 'OPREMA' },
  { id: 'galerija', label: 'GALERIJA' },
  { id: 'booking', label: 'REZERVACIJA' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isRooms = location.pathname.startsWith('/sobe');
  const links = isRooms ? roomLinks : homeLinks;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const goHome = () => {
    setMobileOpen(false);
    navigate('/');
    window.scrollTo({ top: 0 });
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 py-3 md:py-4 glass-nav ${scrolled ? 'nav-solid' : ''}`}>
        <div className="w-full px-5 md:px-10 lg:px-14 flex items-center justify-between gap-4">
          <div className="flex items-center">
            <button
              onClick={goHome}
              className="flex items-center cursor-pointer whitespace-nowrap"
              aria-label="Gostilna Marta – domov"
            >
              <img
                src={LOGO_IMAGE}
                alt="Gostilna Marta – logotip"
                title="Gostilna Marta – tradicionalna slovenska gostilna"
                className="h-8 md:h-11 w-auto object-contain"
              />
            </button>

            <a
              href="tel:+38641331990"
              className="hidden xl:flex items-center gap-2.5 ml-4 pl-4 border-l border-white/20 group cursor-pointer"
              aria-label="Pokličite nas in rezervirajte mizo: 041 331 990"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/15 border border-primary-400/30 group-hover:bg-primary-500/25 transition-colors duration-300">
                <i className="ri-phone-line text-primary-400 text-base"></i>
              </span>
              <span className="flex flex-col leading-tight">
                <span className="font-label text-[10px] tracking-extra-wide text-white/60">
                  POKLIČITE · REZERVIRAJTE MIZO
                </span>
                <span className="font-label text-xs font-semibold text-white group-hover:text-primary-400 transition-colors duration-300 whitespace-nowrap">
                  041 331 990
                </span>
              </span>
            </a>
          </div>

          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            <CategorySwitcher />
            <span className="w-px h-5 bg-white/20"></span>
            <div className="flex items-center gap-4 xl:gap-7">
              {links.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="font-label text-[11px] xl:text-xs font-medium tracking-extra-wide text-white/80 hover:text-primary-400 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-5 xl:gap-6">
            <a
              href="tel:+38627130060"
              className="hidden xl:inline-flex items-center gap-2 font-label text-xs font-medium tracking-extra-wide text-white/85 hover:text-primary-400 transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-primary-400 text-sm"></i>
              +386 2 713 00 60
            </a>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-950 text-xs font-label font-semibold tracking-extra-wide transition-all duration-300 whitespace-nowrap rounded-sm cursor-pointer"
            >
              <i className="ri-map-pin-line"></i>NAVODILA ZA POT
            </a>
          </div>

          <button
            className="lg:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer"
            onClick={() => setMobileOpen(true)}
            aria-label="Odpri meni"
          >
            <span className="block w-6 h-[1.5px] bg-white"></span>
            <span className="block w-6 h-[1.5px] bg-white"></span>
            <span className="block w-6 h-[1.5px] bg-white"></span>
          </button>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-[60] bg-background-950/95 backdrop-blur-xl transition-all duration-500 lg:hidden ${
          mobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-4 right-5 w-11 h-11 flex items-center justify-center text-white/80 hover:text-white cursor-pointer"
          aria-label="Zapri meni"
        >
          <i className="ri-close-line text-3xl"></i>
        </button>
        <div className="flex flex-col items-center justify-center h-full gap-6 px-6">
          <CategorySwitcher variant="mobile" />

          <div className="flex flex-col items-center gap-5 mt-2">
            {links.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="font-heading text-2xl text-white/90 hover:text-primary-400 transition-colors duration-300 cursor-pointer"
              >
                {link.label}
              </button>
            ))}
          </div>

          <a
            href="tel:+38627130060"
            className="mt-2 inline-flex items-center gap-2 font-label text-base text-white/80 hover:text-primary-400 transition-colors duration-300 cursor-pointer"
          >
            <i className="ri-phone-line text-primary-400"></i>
            +386 2 713 00 60
          </a>
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-950 text-sm font-label font-semibold tracking-wide rounded-sm cursor-pointer"
          >
            <i className="ri-map-pin-line"></i>NAVODILA ZA POT
          </a>
        </div>
      </div>
    </>
  );
}